import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link as RouterLink, useParams } from "react-router-dom";
import { api, ApiError } from "../api/client";
import { useApiKey, useAsync } from "../api/hooks";
import { ROUTES } from "../app/routes";
import { FieldControl } from "../features/models/FieldControl";
import { ModelReference } from "../features/models/ModelReference";
import { useI18n } from "../i18n/I18nProvider";
import {
  AlertBanner,
  Badge,
  Button,
  Card,
  CodeBlock,
  Divider,
  Loader,
  MethodBadge,
  PlayIcon,
  Tabs,
  typographyClass,
  useToast,
} from "../ui-kit";
import type { FieldSpec, ModelDetail, TaskResponse } from "../api/types";

type FormValues = Record<string, unknown>;
type TabKey = "form" | "request" | "curl";

/**
 * Начальные значения формы.
 *
 * Порядок: значение по умолчанию из node definition -> пример с бэкенда (нужен
 * прежде всего для промпта, у которого дефолта не бывает) -> пусто.
 * Примеры берём только для обязательных многострочных полей: подставлять
 * выдуманную ссылку в файловый вход нельзя — запуск бы сразу упал.
 */
function initialValues(fields: FieldSpec[], examples: Record<string, unknown>): FormValues {
  const values: FormValues = {};
  for (const field of fields) {
    const example =
      field.required && field.control === "textarea" ? examples[field.name] : undefined;
    values[field.name] = field.default ?? example ?? (field.control === "switch" ? false : "");
  }
  return values;
}

/** Пустые значения в тело не попадают — пусть бэкенд подставит дефолты узла. */
function cleanValues(values: FormValues): Record<string, unknown> {
  const result: Record<string, unknown> = {};
  for (const [key, value] of Object.entries(values)) {
    if (value === "" || value === null || value === undefined) continue;
    if (Array.isArray(value) && value.length === 0) continue;
    result[key] = value;
  }
  return result;
}

const TERMINAL_STATUSES = new Set(["done", "error", "canceled", "failed"]);

export function ModelPage() {
  const { slug = "" } = useParams();
  const { t, modelDoc, language } = useI18n();
  const toast = useToast();

  const model = useAsync<ModelDetail>(() => api.model(slug), [slug]);
  const [inputs, setInputs] = useState<FormValues>({});
  const [params, setParams] = useState<FormValues>({});
  const [tab, setTab] = useState<TabKey>("form");
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [runError, setRunError] = useState<string | null>(null);
  // Запуск и оценка идут от имени пользователя, поэтому без личного ключа они закрыты.
  const apiKey = useApiKey();
  const [running, setRunning] = useState(false);
  const [price, setPrice] = useState<string | null>(null);
  const [task, setTask] = useState<TaskResponse | null>(null);
  const pollRef = useRef<number | null>(null);

  useEffect(() => {
    if (!model.data) return;
    setInputs(initialValues(model.data.inputs, model.data.example_request.inputs));
    setParams(initialValues(model.data.params, model.data.example_request.params));
    setTask(null);
    setPrice(null);
    setRunError(null);
    setFieldErrors({});
  }, [model.data]);

  useEffect(() => () => {
    if (pollRef.current) window.clearInterval(pollRef.current);
  }, []);

  const body = useMemo(
    () => ({ inputs: cleanValues(inputs), params: cleanValues(params) }),
    [inputs, params],
  );

  const doc = model.data ? modelDoc(model.data.id_global) : {};
  const title = doc.name ?? model.data?.name ?? slug;
  const tagline = doc.tagline ?? model.data?.tagline;
  const description = doc.description ?? model.data?.description;

  const handleApiError = useCallback((cause: unknown) => {
    if (cause instanceof ApiError) {
      const nextErrors: Record<string, string> = {};
      for (const item of cause.errors) {
        nextErrors[`${item.kind ?? "param"}:${item.field}`] = item.message;
      }
      setFieldErrors(nextErrors);
      setRunError(cause.errors.length ? null : cause.message);
    } else {
      setRunError(cause instanceof Error ? cause.message : String(cause));
    }
  }, []);

  const startPolling = useCallback(
    (taskId: number) => {
      if (pollRef.current) window.clearInterval(pollRef.current);
      pollRef.current = window.setInterval(async () => {
        try {
          const next = await api.task(taskId);
          setTask(next);
          if (TERMINAL_STATUSES.has(next.status) && pollRef.current) {
            window.clearInterval(pollRef.current);
            pollRef.current = null;
          }
        } catch {
          if (pollRef.current) window.clearInterval(pollRef.current);
          pollRef.current = null;
        }
      }, 3000);
    },
    [],
  );

  const onRun = async () => {
    setRunning(true);
    setFieldErrors({});
    setRunError(null);
    try {
      const result = await api.run(slug, body);
      toast.show({ title: t("toast.runStarted"), subtitle: `task_id ${result.task_id}`, tone: "success" });
      setTask({ task_id: result.task_id, status: "new", position: null, error_message: null, outputs: null });
      startPolling(result.task_id);
    } catch (cause) {
      handleApiError(cause);
      toast.show({ title: t("toast.runFailed"), tone: "error" });
    } finally {
      setRunning(false);
    }
  };

  const onEstimate = async () => {
    setFieldErrors({});
    setRunError(null);
    try {
      const result = await api.estimate(slug, body);
      setPrice(t("model.estimated", { price: result.price, balance: result.credits_balance }));
    } catch (cause) {
      handleApiError(cause);
    }
  };

  if (model.loading) return <Loader label={t("status.loading")} />;
  if (model.error || !model.data) {
    return <AlertBanner tone="error" title={t("status.error")}>{model.error?.message}</AlertBanner>;
  }

  const renderFields = (fields: FieldSpec[], values: FormValues, setValues: (next: FormValues) => void) =>
    fields.map((field) => (
      <FieldControl
        key={field.name}
        idGlobal={model.data!.id_global}
        field={field}
        value={values[field.name]}
        error={fieldErrors[`${field.kind}:${field.name}`]}
        onChange={(value) => setValues({ ...values, [field.name]: value })}
      />
    ));

  const curl = [
    `curl -X POST "$PORTAL_URL/api/models/${model.data.slug}" \\`,
    `  -H "Content-Type: application/json" \\`,
    `  -H "X-Api-Key: $PHYGITAL_API_KEY" \\`,
    `  -d '${JSON.stringify(body)}'`,
  ].join("\n");

  return (
    <>
      <Card>
        <RouterLink to={ROUTES.catalog} className={typographyClass("caption-12-medium")} style={{ color: "var(--color-interaction-violet)", textDecoration: "none" }}>
          ← {t("action.back")}
        </RouterLink>

        {tagline && (
          <span
            className={typographyClass("caption-12-medium")}
            style={{
              color: "var(--color-text-secondary-v2)",
              textTransform: "uppercase",
              letterSpacing: "var(--font-letter-spacing-wide)",
            }}
          >
            {tagline}
          </span>
        )}
        <h1 className={typographyClass("header-30-semibold")}>{title}</h1>
        <p className={typographyClass("text-14-medium")} style={{ color: "var(--color-text-secondary-v2)", maxWidth: "68ch" }}>
          {description || t("model.noDescription")}
        </p>

        {/* Модель остаётся рабочей, поэтому это предупреждение, а не блокировка:
            существующие интеграции продолжают жить, новые лучше не начинать. */}
        {model.data.deprecated && (
          <AlertBanner tone="warning" title={t("model.deprecated")}>
            {t("model.deprecatedHint")}
          </AlertBanner>
        )}

        <div style={{ display: "flex", flexWrap: "wrap", gap: "var(--spacing-3)", alignItems: "center" }}>
          <MethodBadge method="POST" />
          <code className={typographyClass("mono-12")}>/api/models/{model.data.slug}</code>
          {model.data.average_time_seconds != null && (
            <>
              <Divider />
              <Badge tone="grey" size="small">
                {t("model.averageTime")}: {t("model.seconds", { value: model.data.average_time_seconds })}
              </Badge>
            </>
          )}
        </div>
      </Card>

      <ModelReference model={model.data} />

      <Card>
        <Tabs<TabKey>
          value={tab}
          onChange={setTab}
          items={[
            { value: "form", label: t("model.tryIt") },
            { value: "request", label: t("model.request") },
            { value: "curl", label: "cURL" },
          ]}
        />

        {tab === "form" && (
          <>
            {model.data.inputs.length > 0 && (
              <>
                <h2 className={typographyClass("header-16-semibold")}>{t("model.inputs")}</h2>
                {renderFields(model.data.inputs, inputs, setInputs)}
              </>
            )}
            {model.data.params.length > 0 && (
              <>
                <h2 className={typographyClass("header-16-semibold")} style={{ marginTop: "var(--spacing-4)" }}>
                  {t("model.params")}
                </h2>
                {renderFields(model.data.params, params, setParams)}
              </>
            )}

            {/* Запуск и оценка — действия над формой, идут вместе слева.
                Сброс формы к ним не относится, поэтому отделён и без заливки. */}
            <div
              style={{
                display: "flex",
                gap: "var(--spacing-2-5)",
                flexWrap: "wrap",
                alignItems: "center",
                marginTop: "var(--spacing-4)",
              }}
            >
              <Button
                endIcon={<PlayIcon />}
                loading={running}
                onClick={onRun}
                disabled={!apiKey}
                title={apiKey ? undefined : t("apiKey.requiredHint")}
              >
                {t("action.run")}
              </Button>
              <Button
                variant="tertiary"
                onClick={onEstimate}
                disabled={!apiKey}
                title={apiKey ? undefined : t("apiKey.requiredHint")}
              >
                {t("action.estimate")}
              </Button>
              <Button
                variant="ghost"
                style={{ marginLeft: "auto" }}
                onClick={() => {
                  setInputs(initialValues(model.data!.inputs, model.data!.example_request.inputs));
                  setParams(initialValues(model.data!.params, model.data!.example_request.params));
                }}
              >
                {t("action.reset")}
              </Button>
            </div>

            {!apiKey && (
              <AlertBanner tone="warning" title={t("apiKey.requiredTitle")}>
                {t("apiKey.requiredText")}
              </AlertBanner>
            )}
            {price && <AlertBanner tone="info">{price}</AlertBanner>}
            {runError && <AlertBanner tone="error" title={t("status.error")}>{runError}</AlertBanner>}
          </>
        )}

        {tab === "request" && <CodeBlock code={JSON.stringify(body, null, 2)} copyLabel={t("action.copy")} />}
        {tab === "curl" && <CodeBlock code={curl} copyLabel={t("action.copy")} />}
      </Card>

      {task && (
        <Card>
          <h2 className={typographyClass("header-16-semibold")}>{t("task.title")}</h2>
          <div style={{ display: "flex", gap: "var(--spacing-2)", flexWrap: "wrap", alignItems: "center" }}>
            <Badge tone="grey" size="small">#{task.task_id}</Badge>
            <Badge tone={task.status === "done" ? "positive" : task.status === "error" ? "error" : "default"} size="small">
              {task.status}
            </Badge>
            {task.position != null && <Badge tone="outline" size="small">#{task.position}</Badge>}
            {!TERMINAL_STATUSES.has(task.status) && <Loader size="small" label={t("task.polling")} />}
          </div>
          {task.error_message && <AlertBanner tone="error" title={t("task.error")}>{task.error_message}</AlertBanner>}
          {task.outputs && <CodeBlock code={JSON.stringify(task.outputs, null, 2)} copyLabel={t("action.copy")} />}
        </Card>
      )}

      {/* Язык влияет на подписи полей — ключ форсирует перерисовку словарей. */}
      <span hidden data-language={language} />
    </>
  );
}
